<template>
  <div v-if="state.rules.visible" class="hr-field-upload">
    <div class="hr-field-upload__title">{{ modelValue.label }}</div>
    <div class="hr-field-upload__description">
      {{ modelValue.description }}
    </div>
    <div v-if="state.loading">loading...</div>
    <CoUpload
      v-else
      drag
      type="drag"
      :file-list="state.localFileList || fileList"
      :sizeLimit="20"
      :accepted-type="[
        'application/pdf',
        'image/heic',
        'image/png',
        'image/jpeg',
        'image/jpg',
      ]"
      :upload-function="uploadFunction"
      :remove-function="removeFile"
      :upload-status="uploadStatus"
      preview
    />
    <div v-if="errorMessage" class="hr-field-upload__error">
      {{ errorMessage }}
    </div>
    <div v-if="uploadError" class="hr-field-upload__error">
      {{ uploadError }}
    </div>
    <div v-if="deleteError" class="hr-field-upload__error">
      {{ deleteError }}
    </div>

    <HrInputDate
      v-if="state.dateValue.isRequired"
      :label="'Expiry'"
      :date="state.dateValue.value"
      :placeholder="
        DATE_FORMAT_PLACEHOLDER?.[store.state.onboarding.countryCode] ||
        DATE_FORMAT_PLACEHOLDER.default
      "
      :required="state.dateValue.isRequired"
      :error="state.dateValue.error"
      :disabledDates="disabledDates"
      :country-code="store.state.onboarding.countryCode"
      @date-change="updateDateValue"
    />
  </div>
</template>

<script>
import { CoUpload } from '@deputy/copilot2';
import { reactive } from 'vue';
import mobileBrowserDetection from '@/lib/mobile-browser-detection';
import { getFileUpload } from '@/core/svc-client/file-upload';
import store from '@/store';
import t from '@/lib/i18next';
import { computed, watch } from 'vue';
import rulesFunctions from '@/store/composables/document-rules';
import { FILE_REQUEST_STATUS } from '@/store/modules/FileHandler';
import { UPLOAD_STATUSES } from '@/modules/definitions';
import {
  SUPERANNUATION,
  TAX_DETAILS,
  WORK_RIGHTS_UK_FIELDS,
  WORK_RIGHTS_AU_FIELDS,
} from '@/core/field-names';
import HrInputDate from '@/components/hr-input-date.vue';
import { DateTime } from 'luxon';
import { COUNTRY } from '@/core/country';
import { DATE_FORMAT_PLACEHOLDER } from '@/core/date-format';
import { logWebHrError } from '@/core/logger/log-wrapper';

const bytesInMB = 1000000;

export default {
  name: 'hr-field-upload',
  components: {
    CoUpload,
    HrInputDate,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => ({ help: { hint: 'description' } }),
    },
    errorMessage: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const state = reactive({
      field: props.modelValue,
      isMobile: !!mobileBrowserDetection(),
      localFileList: null,
      loading: false,
      fieldRules: computed(() => store.state.Document.fieldRules),
      rules: {
        visible: rulesFunctions.visible.default(),
        disabled: rulesFunctions.disabled.default(),
        override: rulesFunctions.override.default(),
      },
      file: props.modelValue.fileValue,
      dateValue: {
        label: 'Expiry',
        value: props.modelValue.fileValue.expiredAt,
        isRequired: props.modelValue.hasExpiry,
        error: computed(() => props.modelValue.dateError),
      },
    });

    const TODAY = DateTime.now();
    const disabledDates = { to: TODAY.toJSDate() };

    const fileId = store.getters['FileHandler/getFileId']({
      fieldName: state.field.name,
      fieldId: state.field.id,
    });

    const uploadError = computed(
      () => store.state.FileHandler.uploadErrors[fileId]?.message
    );
    const deleteError = computed(
      () => store.state.FileHandler.deleteErrors[fileId]?.message
    );

    const uploadStatusMap = {
      [FILE_REQUEST_STATUS.PENDING]: UPLOAD_STATUSES.PENDING,
      [FILE_REQUEST_STATUS.PROCESSING]: UPLOAD_STATUSES.UPLOADING,
      [FILE_REQUEST_STATUS.SUCCESS]: UPLOAD_STATUSES.UPLOADED,
      [FILE_REQUEST_STATUS.FAILED]: UPLOAD_STATUSES.ERROR,
    };

    const uploadStatus = computed(
      () => uploadStatusMap[store.state.FileHandler.uploadStatuses[fileId]]
    );

    function isFileValueEmpty(fileValueObj) {
      return Object.keys(fileValueObj || []).length === 0;
    }

    const fileValue = computed(() => {
      const fileStateFromStore = store.state.FileHandler.fileStates[fileId];
      return isFileValueEmpty(props.modelValue.fileValue)
        ? (isFileValueEmpty(fileStateFromStore) && []) || [fileStateFromStore]
        : [props.modelValue.fileValue];
    });

    function input(newFileValue) {
      const allSystemDocuments = [
        SUPERANNUATION.SELF_MANAGED_FORM,
        TAX_DETAILS.P45_UPLOAD,
        WORK_RIGHTS_AU_FIELDS.BIRTH_CERTIFICATE,
        WORK_RIGHTS_AU_FIELDS.PHOTO_ID,
        WORK_RIGHTS_AU_FIELDS.PASSPORT,
        WORK_RIGHTS_UK_FIELDS.BIRTH_CERTIFICATE,
        WORK_RIGHTS_UK_FIELDS.PASSPORT,
        WORK_RIGHTS_UK_FIELDS.SUPPORTING_DOCUMENTS,
      ];
      const isSystemDoc = allSystemDocuments.includes(state.field.name);

      let updatedValue = {
        ...props.modelValue,
        fileValue: {
          ...props.modelValue.fileValue,
          ...(newFileValue && {
            name: newFileValue.name || null,
            sizeInBytes: newFileValue.sizeInBytes || null,
            content: newFileValue.content || null,
          }),
        },
      };

      if (isSystemDoc) {
        if (isFileValueEmpty(newFileValue)) {
          updatedValue = {
            ...props.modelValue,
            fileValue: {},
          };
        }
      }

      if (updatedValue.requiredValues?.hasFile !== undefined) {
        if (Object.keys(newFileValue || []).length > 0) {
          updatedValue.requiredValues.hasFile = true;
        } else {
          updatedValue.requiredValues.hasFile = false;
        }
      }

      if (
        [
          WORK_RIGHTS_UK_FIELDS.PASSPORT,
          WORK_RIGHTS_AU_FIELDS.PASSPORT,
          WORK_RIGHTS_AU_FIELDS.PHOTO_ID,
        ].includes(state.field.name) &&
        !updatedValue.fileValue.expiredAt
      ) {
        updatedValue.requiredValues.expiredAt = false;
      }

      emit('update', updatedValue);
    }
    watch(
      () => store.state.documents,
      () => {
        checkRules();
      },
      {
        deep: true,
      }
    );
    watch(
      () => store.state.Document.fieldRulesTriggered,
      () => {
        checkRules();
      },
      {
        deep: true,
      }
    );

    watch(
      () => store.state.FileHandler.uploadStatuses[fileId],
      () => {
        const status = store.state.FileHandler.uploadStatuses[fileId];
        state.file = store.state.FileHandler.fileStates[fileId];
        if (status === FILE_REQUEST_STATUS.SUCCESS) {
          input(state.file);
        }
      }
    );

    const fileList = computed(() => {
      if (!fileValue.value.sizeInBytes) {
        return [];
      }
      return JSON.parse(JSON.stringify(fileValue.value));
    });

    function checkRules() {
      const rules = state.fieldRules[state.field.name];
      if (rules) {
        const rulesIndexed = {};
        Object.keys(rules).forEach((target) => {
          const fieldRules = rules[target];

          fieldRules.forEach((fieldRule) => {
            rulesIndexed[fieldRule.type] = rulesIndexed[fieldRule.type] || [];
            rulesIndexed[fieldRule.type].push({
              value: store.getters['Document/getFieldValue'](target),
              rule: fieldRule,
            });
          });
        });
        Object.keys(rulesIndexed).forEach((rule) => {
          state.rules[rule] = rulesFunctions[rule].apply(rulesIndexed[rule]);
          state.field.appliedRules[rule] = state.rules[rule];
          store.dispatch('setFieldRules', state.field);
        });
      }
    }

    function uploadFunction(fileInfo) {
      store.dispatch('FileHandler/upload', {
        fieldName: props.modelValue.name,
        fieldId: props.modelValue.id,
        fileInfo,
        fileLabel: props.modelValue.label,
        onboardingId: store.state.onboardingId,
      });
    }

    function removeFile() {
      store.dispatch('FileHandler/delete', {
        fieldId: props.modelValue.id,
        onboardingId: store.state.onboardingId,
        fileId,
      });
      input({});

      if (state.dateValue.value) {
        updateDateValue(null, false);
      }
    }
    function buildRules() {
      const { rules } = state.field.validation;

      rules.forEach((rule) => {
        if (rulesFunctions[rule.type]) {
          state.rules[rule.type] = rulesFunctions[rule.type].default();
        }

        store.dispatch(
          'Document/addFieldRule',
          Object.assign({}, rule, { fieldName: state.field.name })
        );
      });
    }
    async function initialize() {
      if (state.field.validation && state.field.validation.rules) {
        // TODO:  here is adding repeated rules, a hack is implemented in the document store
        buildRules();
      }

      if (
        [FILE_REQUEST_STATUS.SUCCESS, FILE_REQUEST_STATUS.FAILED].includes(
          uploadStatus.value
        )
      ) {
        return;
      }

      try {
        // TODO: move this to FileHandler and read it from there
        state.loading = true;
        const apiContent =
          (await getFileUpload(
            store.state.onboardingId,
            props.modelValue.id
          )) || {};

        state.localFileList = [
          {
            ...props.modelValue.fileValue,
            ...apiContent,
            base64: apiContent.content,
            sizeInMB: (apiContent.sizeInBytes / bytesInMB).toFixed(2),
          },
        ];

        if (state.dateValue.value) {
          updateDateValue(state.dateValue.value);
        }
      } catch (e) {
        if (e?.response?.status !== 404) {
          logWebHrError({
            message: 'Failed to initialize File Upload',
            source: 'hr-field-upload',
            metadata: {
              hasFileValue: !!props.modelValue?.fileValue,
              onboardingId: store.state?.onboardingId,
              dateValue: state.dateValue?.value,
            },
            error: e,
          });
        }

        state.localFileList = null;
      } finally {
        state.loading = false;
      }
    }

    function isDateLesserThanMinDate() {
      return (
        DateTime.fromJSDate(new Date(state.dateValue.value)) <
        DateTime.fromJSDate(disabledDates.to)
      );
    }

    function validateDate() {
      let error = '';
      if (
        !state.dateValue.value &&
        state.field.name !== WORK_RIGHTS_UK_FIELDS.SUPPORTING_DOCUMENTS
      ) {
        error = t('hr-field-upload:required-expiry');
      } else if (isDateLesserThanMinDate()) {
        error = t('hr-field-upload:invalid-expiry');
      }

      return error;
    }

    function updateDateValue(val, hasFileValue = true) {
      state.dateValue.value = val;
      if (
        (!val &&
          [
            WORK_RIGHTS_UK_FIELDS.PASSPORT,
            WORK_RIGHTS_AU_FIELDS.PASSPORT,
            WORK_RIGHTS_AU_FIELDS.PHOTO_ID,
          ].includes(state.field.name) &&
          !props.modelValue?.fileValue?.sizeInBytes) ||
        (!val &&
          ([
            WORK_RIGHTS_UK_FIELDS.PASSPORT,
            WORK_RIGHTS_AU_FIELDS.PASSPORT,
            WORK_RIGHTS_AU_FIELDS.PHOTO_ID,
          ].includes(state.field.name) ||
            state.field.name === WORK_RIGHTS_UK_FIELDS.SUPPORTING_DOCUMENTS) &&
          !hasFileValue)
      ) {
        return;
      }

      const dateError = validateDate();

      const updatedVal = {
        ...props.modelValue,
        fileValue: {
          ...props.modelValue.fileValue,
          expiredAt: state.dateValue.value,
        },
        dateError: dateError,
      };

      if (updatedVal.requiredValues.expiredAt !== undefined) {
        if (!val || !!dateError) {
          updatedVal.requiredValues.expiredAt = false;
        } else {
          updatedVal.requiredValues.expiredAt = true;
        }

        if (state.field.name === WORK_RIGHTS_UK_FIELDS.SUPPORTING_DOCUMENTS) {
          updatedVal.requiredValues.expiredAt = true;
        }
      }

      emit('update', updatedVal);
    }

    initialize();
    return {
      t,
      state,
      store,
      COUNTRY,
      DATE_FORMAT_PLACEHOLDER,
      input,
      uploadFunction,
      removeFile,
      uploadError,
      deleteError,
      fileList,
      uploadStatus,
      blur,
      updateDateValue,
      disabledDates,
    };
  },
};
</script>

<style lang="scss" scoped>
.hr-field-upload {
  display: flex;
  justify-content: center;
  flex-direction: column;
}

.hr-field-upload__title {
  font-weight: 600;
  font-size: 1em;
}

.hr-field-upload__description {
  margin-bottom: 16px;
}

.hr-field-upload__error {
  color: var(--co-status-red);
}

@media only screen and (max-width: 768px) {
  .hr-field-upload {
    font-size: 16px;
  }
  .hr-field-upload :deep(.co-upload-dragger) {
    width: 100%;
  }
  .hr-field-upload :deep(.co-upload-container) {
    width: 100%;
  }
}

::v-deep(svg.co-icon.co-upload-icon--delete) {
  width: 15px !important;
  height: 15px !important;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
